import React, { useState, useRef, useEffect } from 'react';
import { Upload, Clipboard, FileText } from 'lucide-react';

import {
  Modal,
  Textarea,
  FormField,
  Button,
  Divider,
} from "../../design-system";
import { cn } from "../../design-system/utils/cn";

const MarkdownModal = ({ isOpen, onClose, onInsert }) => {
  const [markdownContent, setMarkdownContent] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState('');
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (isOpen && textareaRef.current) {
      textareaRef.current.focus();
      // Auto-collage si le presse-papiers contient du Markdown
      navigator.clipboard?.readText?.().then((text) => {
        if (text && (text.includes('#') || text.includes('*') || text.includes('`') || text.includes('['))) {
          setMarkdownContent(text);
        }
      }).catch(() => {});
    } else if (!isOpen) {
      setMarkdownContent('');
      setFileName('');
      setIsDragging(false);
    }
  }, [isOpen]);

  const handleClose = () => onClose();

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (markdownContent.trim()) {
      onInsert(markdownContent.trim());
      handleClose();
    }
  };

  const readFile = (file) => {
    if (!file.name.endsWith('.md') && file.type !== 'text/markdown' && file.type !== 'text/plain') {
      alert('Veuillez sélectionner un fichier Markdown (.md) valide.');
      return;
    }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => setMarkdownContent(event.target.result);
    reader.onerror = () => alert('Erreur lors de la lecture du fichier.');
    reader.readAsText(file);
  };

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) readFile(file);
  };
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) readFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handlePasteButton = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        setMarkdownContent(text);
        textareaRef.current?.focus();
      }
    } catch {
      alert('Impossible d\'accéder au presse-papiers. Colle manuellement (Ctrl+V).');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="xl"
      title="Importer du Markdown"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose}>Annuler</Button>
          <Button
            onClick={handleSubmit}
            isDisabled={!markdownContent.trim()}
            leftIcon={<FileText className="w-4 h-4" strokeWidth={1.8} />}
          >
            Importer
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormField label="Coller votre Markdown" helper="Tu peux coller directement (Ctrl+V) ou utiliser le bouton Coller.">
          <div className="relative">
            <Textarea
              ref={textareaRef}
              placeholder="Colle ton contenu Markdown ici…"
              value={markdownContent}
              onChange={(e) => setMarkdownContent(e.target.value)}
              rows={8}
              autoFocus
              className="font-mono text-[12.5px] pr-[100px]"
            />
            <button
              type="button"
              onClick={handlePasteButton}
              className="absolute top-2 right-2 inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-[12px] font-medium bg-cream-2 text-text hover:bg-cream-3 border border-cream-3 transition-colors"
              title="Coller depuis le presse-papiers"
            >
              <Clipboard className="w-3.5 h-3.5" strokeWidth={1.8} />
              Coller
            </button>
          </div>
        </FormField>

        <div className="relative flex items-center gap-3">
          <Divider className="flex-1" />
          <span className="text-[10.5px] font-mono uppercase tracking-wider text-mute">ou</span>
          <Divider className="flex-1" />
        </div>

        <FormField label="Glisser-déposer ou sélectionner un fichier" helper="Fichiers Markdown (.md) uniquement.">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={cn(
              'w-full flex flex-col items-center justify-center gap-2 py-6 px-4 rounded-md border-2 border-dashed transition-colors text-mute hover:text-text hover:bg-bg-2',
              isDragging ? 'border-ink bg-cream-2/40 text-text' : 'border-border',
            )}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".md,text/markdown,text/plain"
              onChange={handleFileSelect}
              className="sr-only"
            />
            <Upload className="w-6 h-6" strokeWidth={1.6} />
            <span className="text-sm font-medium">
              {isDragging ? 'Déposez le fichier ici' : 'Glissez un .md ou cliquez pour sélectionner'}
            </span>
            {fileName && (
              <span className="text-[11px] font-mono text-ink mt-1">
                ✓ {fileName}
              </span>
            )}
          </button>
        </FormField>
      </form>
    </Modal>
  );
};

export default MarkdownModal;
